<template>
  <div class="property-appraisal">
    <div class="form-group">
      <p>Appraisal information</p>

      <value-input
        :valueInputIds="['exact_d', 'greater_d', 'range_d','notprovided']"
        :label="form.appraisalType.label"
        @validated-fields="validate"
        @input="onInput"
      />

      <value-input
        class="subsection"
        :valueInputIds="['fixed_p', 'fixed_d', 'range_p','accelerator','notprovided']"
        :label="form.commissionModel.label"
        @validated-fields="validate"
        @input="onInput"
      />

      <value-input
        class="subsection"
        :valueInputIds="['includegst', 'excludegst']"
        :label="form.commissionGST.label"
        @validated-fields="validate"
        @input="onInput"
      />

      <value-input
        class="subsection"
        :valueInputIds="['fixed_d','range_d','incommission','free', 'notprovided']"
        :label="form.marketingCost.label"
        @validated-fields="validate"
        @input="onInput"
      />

      <div class="subsection">
        <label for="comments">Comments</label>
        <textarea id="comments" rows="5" v-model="comment" />
      </div>

      <div class="subsection">
        <oa-button
          :text="'Submit & mark as appraised'"
          :btnClass="{ color: 'green' }"
          :disabled="disableValidateButton"
          @click="clickedValidate"
          gacategory="Lead"
        />
      </div>
    </div>
  </div>
</template>

<script>

import { mapState, mapActions, mapGetters } from 'vuex';
import { LeadAgentLeadStageType, LeadAgentLeadStageTypeId } from '@/Mapper/LeadAgentLeadStageType';
import OaButton from '@/components/Forms/OaButton';
import ValueInput from '@/components/Forms/ValueInput.vue';
import StageUpdate from '@/mixins/StageUpdate';
import FormHandler from '@/mixins/FormHandler';

export default {
  name: 'PropertyAppraisal',
  mixins: [StageUpdate, FormHandler],
  components: {
    OaButton,
    ValueInput,
  },
  computed: {
    ...mapState({
      agent: state => state.agentStore.agent,
      user: state => state.userStore.user,
      lam: state => state.leadAgentMappingStore.lam,
      lead: state => state.leadAgentMappingStore.lead,
    }),
    ...mapGetters('leadAgentMappingStore', [
      'lamStageIsLessThanAppraised',
    ]),
  },
  data() {
    return {
      disableSubmit: false,
      comment: '',
      form: {
        appraisalType: {
          label: 'Appraisal type',
          values: {},
        },
        commissionModel: {
          label: 'Commission model',
          values: {},
        },
        marketingCost: {
          label: 'Marketing costs',
          values: {},
        },
        commissionGST: {
          label: 'Commission GST',
          values: {},
        },
      },
    };
  },
  methods: {
    ...mapActions('leadAgentMappingStore', [
      'updateLeadAgentMapping',
    ]),
    getAppraisalTypeId() {
      // Appraisal types 1=exact, 2=greater than, 3=range, 4=not provided
      switch (this.form.appraisalType.values.select) {
        case 'exact_d':
          return 1;
        case 'greater_d':
          return 2;
        case 'range_d':
          return 3;
        default:
          return 4;
      }
    },
    getCommissionModelId() {
      switch (this.form.commissionModel.values.select) {
        case 'fixed_p':
          return 1;
        case 'fixed_d':
          return 2;
        case 'range_p':
          return 3;
        case 'accelerator':
          return 4;
        case 'notprovided':
          return 6;
        default:
          return 0;
      }
    },
    getMarketingCostId() {
      switch (this.form.marketingCost.values.select) {
        case 'fixed_d':
          return 1;
        case 'range_d':
          return 2;
        case 'incommission':
          return 3;
        case 'free':
          return 4;
        case 'notprovided':
          return 5;
        default:
          return '';
      }
    },
    createCaseForAppraisalUpdate() {
      const caseData = {
        leadkey: this.lead.key,
        agentkey: this.$route.params.ak,
        // agentkey: this.agent.key,
        leadstatus: LeadAgentLeadStageTypeId[LeadAgentLeadStageType.Appraised],
        comment: this.comment,
      };
      return this.$apiServices.oa.lead.createCaseForLeadUpdate(caseData);
    },
    saveDetails() {
      this.disableSubmit = true;
      this.saveAppraisalDetails()
        .then(() => {
          this.saveCommissionDetails()
            .then(() => {
              this.saveMarketingDetails()
                .then(() => {
                  if (this.lamStageIsLessThanAppraised) {
                    this.moveToAppraised()
                      .then(() => {
                        this.logLeadEvent(
                          'Leads Management - Submit status change - Appraised',
                          'Single Deal Page - Post-Accepted',
                        );
                        if (this.comment && this.comment.length > 0) {
                          this.createCaseForAppraisalUpdate();
                        }
                        this.disableSubmit = false;
                      });
                  } else {
                    this.disableSubmit = false;
                  }
                });
            });
        });
    },
    saveAppraisalDetails() {
      const data = {
        leadagentmappingid: this.lam.id,
        oauserkey: !!this.user && !!this.user.key ? this.user.key : this.agent.userkey,
        // oauserkey: this.agent.userkey,
        leadagentleadstagetypeid: LeadAgentLeadStageType.Appraised,
        appraisaltypeid: this.getAppraisalTypeId(),
        appraisallow: this.form.appraisalType.values.value1,
        appraisalhigh: this.form.appraisalType.values.value2,
        appraisaldetail: this.comment,
        sourcetypeid: this.isLamOwner ? 1 : 5,
      };

      return this.$apiServices.oa.leadAgent.saveAppraisalDetails(data);
    },
    saveCommissionDetails() {
      const data = {
        leadagentmappingid: this.lam.id,
        oauserkey: !!this.user && !!this.user.key ? this.user.key : this.agent.userkey,
        // oauserkey: this.agent.userkey,
        leadagentleadstagetypeid: LeadAgentLeadStageType.Appraised,
        commissiontypeid: this.getCommissionModelId(),
        commissionamountlow: this.form.commissionModel.values.value1,
        commissionamounthigh: this.form.commissionModel.values.value2,
        includesgst: (this.form.commissionGST.values.select === 'includegst' ? '1' : '0'),
        sourcetypeid: this.isLamOwner ? 1 : 5,
      };

      // if commission not provided, unset gst type
      if (([0, 6].includes(data.commissiontypeid))) {
        data.includesgst = '';
      }

      return this.$apiServices.oa.leadAgent.saveCommissionDetails(data);
    },
    saveMarketingDetails() {
      const data = {
        leadagentmappingid: this.lam.id,
        oauserkey: !!this.user && !!this.user.key ? this.user.key : this.agent.userkey,
        // oauserkey: this.agent.userkey,
        leadagentleadstagetypeid: LeadAgentLeadStageType.Appraised,
        marketingcosttypeid: this.getMarketingCostId(),
        marketingcostamount: ['fixed_d'].includes(this.form.marketingCost.values.select)
          ? this.form.marketingCost.values.value1
          : '',
        marketingcostamountlow: ['range_d'].includes(this.form.marketingCost.values.select)
          ? this.form.marketingCost.values.value1
          : '',
        marketingcostamounthigh: ['range_d'].includes(this.form.marketingCost.values.select)
          ? this.form.marketingCost.values.value2
          : '',
        sourcetypeid: this.isLamOwner ? 1 : 5,
      };

      return this.$apiServices.oa.leadAgent.saveMarketingCostDetails(data);
    },
    moveToAppraised() {
      const data = {
        stagetypeid: LeadAgentLeadStageType.Appraised,
        sourcetypeid: this.isLamOwner ? 1 : 5,
      };
      return this.updateLeadAgentStage(data);
    },
    updateLeadAgentStage(data) {
      if (!data.oauserid && !!this.user) {
        data.oauserid = this.user.id;
        data.oauserkey = this.user.key;
      }
      data.sourcetypeid = this.isLamOwner ? 1 : 5;
      const payload = {
        lamid: this.lam.id,
        data,
      };
      return this.updateLeadAgentMapping(payload);
    },
  },
};

</script>

<style lang="scss" scoped>

.property-appraisal .btn {
  margin-bottom: 15px;
}

.property-appraisal .subsection {
  @apply ut-pt-6;
}

.property-appraisal p {
  @apply ut-font-normal ut-text-lg ut-pb-3;
  line-height: 28px;
}

</style>
